import {useCallback, useMemo, useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {
  ActivityIndicator,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import {useSafeAreaFrame, useSafeAreaInsets} from 'react-native-safe-area-context';
import {getListFromPages} from '~components/HomeScreenComponent/helpers/getListFromPages';
import {useDebounce} from '~components/SearchBarWithAutocomplete';
import {useGetOrderList} from '~hooks/event/useGetOrderList';
import {Event, OrderDetail} from '~types/api/event';
import {EVENTS_TIMEFRAME, ORDER_BY, ORDER_DIR, TABS} from '~types/events';
import {responsive, spacing} from '~constants';
import ModernEventsList from '../components/ModernEventsList';
import {useTheme} from '~contexts/ThemeContext';
import ModernSpinner from '~components/ModernSpinner/ModernSpinner';

const PersonalEvents = ({
  timeframe,
  isBusiness,
  scrollHandler,
  filterEvents,
  selectedTimeframe,
  globalInputValue,
  type,
  isNeighbourhood,
}: {
  timeframe: EVENTS_TIMEFRAME | null;
  isBusiness?: boolean;
  scrollHandler: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  filterEvents: (data: any, selectedTimeframe: {id: number; title: string}) => any;
  selectedTimeframe: {id: number; title: string};
  globalInputValue: string;
  type?: string;
  isNeighbourhood?: boolean;
}) => {
  const [searchText, setSearchText] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const {colors} = useTheme();

  // Get user's orders/tickets instead of all events
  const {data: orderList, isLoading, refetch} = useGetOrderList();
  const orders = orderList as OrderDetail[] | undefined;
  const {top} = useSafeAreaInsets();
  const {width, height} = useSafeAreaFrame();
  const {t} = useTranslation();

  // Convert orders to events list for compatibility, keeping order information
  const eventsWithOrderInfo = useMemo(() => {
    if (!orders) return [];
    return orders.map(order => ({
      ...order.event,
      order_id: order.order_id, // Add order_id to the event object
    }));
  }, [orders]);

  // SIMPLE: Only refetch when type changes
  useEffect(() => {
    refetch();
  }, [type, refetch]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    setTimeout(() => {
      refetch();
      setRefreshing(false);
    }, 2000);
  }, [refetch]);

  // CLIENT-SIDE FILTERING FOR ORDERS BASED ON STATUS AND TIME
  const filteredEvents = useMemo(() => {
    if (!orders) return [];

    let filteredOrders = [...orders];
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today

    // 1. FILTER BY ORDER STATUS - show succeeded and pending orders (completed and processing bookings)
    filteredOrders = filteredOrders.filter(order => 
      order.status === 'succeeded'
    );

    // 2. SEARCH FILTER - filter by event name or description if provided
    if (searchText && searchText.trim()) {
      const searchTerm = searchText.toLowerCase().trim();
      filteredOrders = filteredOrders.filter(
        order =>
          order.event.name.toLowerCase().includes(searchTerm) ||
          (order.event.description && order.event.description.toLowerCase().includes(searchTerm)),
      );
    }

    // 3. TIME FILTER - based on event type (upcoming vs past)
    if (type === 'booked_upcoming') {
      // Show upcoming events (events that haven't ended yet)
      filteredOrders = filteredOrders.filter(order => {
        const endDate = new Date(order.event.end_date);
        return endDate > today; // Event hasn't ended yet
      });
      
      // Sort upcoming events by start date (earliest first)
      filteredOrders.sort((a, b) => {
        const startDateA = new Date(a.event.start_date);
        const startDateB = new Date(b.event.start_date);
        return startDateA.getTime() - startDateB.getTime();
      });
    } else if (type === 'booked_past') {
      // Show past events (events that have ended)
      filteredOrders = filteredOrders.filter(order => {
        const endDate = new Date(order.event.end_date);
        return endDate <= today; // Event has ended
      });
      
      // Sort past events by end date (most recent first)
      filteredOrders.sort((a, b) => {
        const endDateA = new Date(a.event.end_date);
        const endDateB = new Date(b.event.end_date);
        return endDateB.getTime() - endDateA.getTime();
      });
    }

    console.log(`🔍 [MY_BOOKED_EVENTS] ${type}: Filtered ${filteredOrders.length} orders from ${orders.length} total`);
    
    // Return events with order information from the filtered orders
    return filteredOrders.map(order => ({
      ...order.event,
      order_id: order.order_id, // Add order_id to the event object
    }));
  }, [orders, searchText, type]);

  const contentLayout = useMemo(() => {
    const platformLayout = Platform.OS === 'ios' ? 115 : 125;
    return platformLayout + top;
  }, [top]);

  const content = useMemo(() => {
    // Show loading spinner when initially loading and no orders yet
    if (isLoading && (!orders || orders.length === 0)) {
      return (
        <View
          style={{
            flex: 1,
            backgroundColor: colors.background,
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <ModernSpinner size={40} variant="circular" />
        </View>
      );
    }

    return (
      <ModernEventsList
        data={filteredEvents as any} // Type assertion to handle EventA vs Event difference
        isLoading={isLoading}
        isFetching={false} // No pagination, so no fetching state
        isFetchingNextPage={false} // No pagination
        refreshing={refreshing}
        onRefresh={onRefresh}
        onEndReached={undefined} // No pagination
        onEndReachedThreshold={0.5}
        isMyEvents={true}
        emptyStateTitle={
          type === 'booked_upcoming' 
            ? t('home.empty_list_upcoming_events') 
            : t('home.empty_list_past_events')
        }
        emptyStateSubtitle={
          type === 'booked_upcoming' 
            ? t('home.upcoming_empty_string')
            : t('home.past_empty_string')
        }
        estimatedItemSize={230} // Reduced size for more compact cards
        contentContainerStyle={{
          paddingTop: 0, // Remove top padding since we have search bar
        }}
      />
    );
  }, [
    orders,
    isLoading,
    t,
    filteredEvents,
    refreshing,
    onRefresh,
    colors.background,
    type,
  ]);

  return (
    <View
      style={{
        paddingTop: contentLayout,
        height: '100%',
        backgroundColor: colors.background,
      }}>
      {isBusiness && (
        <View
          style={{
            paddingHorizontal: responsive.getValue({xs: spacing.md, sm: spacing.lg, md: spacing.xl}, spacing.lg),
            paddingBottom: spacing.sm,
          }}>
          <TextInput
            style={{
              paddingVertical: spacing.md,
              paddingHorizontal: spacing.lg,
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.border,
              borderRadius: responsive.getValue({xs: 8, sm: 10, md: 12}, 10),
              color: colors.textPrimary,
              fontSize: responsive.getValue({xs: 14, sm: 16, md: 18}, 16),
              shadowColor: colors.black,
              shadowOffset: {width: 0, height: 1},
              shadowOpacity: 0.05,
              shadowRadius: 2,
              elevation: 2,
            }}
            placeholder="Search by title"
            placeholderTextColor={colors.textTertiary}
            value={searchText}
            onChangeText={value => setSearchText(value)}
            returnKeyType="search"
          />
        </View>
      )}
      <View style={{flex: 1}}>{content}</View>
    </View>
  );
};

export default PersonalEvents;
